import { lazy, Suspense, useEffect, useRef, useState } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
  useRouteError,
} from "react-router-dom";

import { DevicesPage } from "@/features/devices/pages/DevicesPage";
import { DeviceDashboardPage } from "@/features/devices/pages/DeviceDashboardPage";
import { WorkspaceDevicesPage } from "@/features/devices/pages/WorkspaceDevicesPage";
import { MachineDashboardPage } from "@/features/machines/pages/MachineDashboardPage";
import { WorkspacePage } from "@/features/workspaces/pages/WorkspacePage";
import { WorkspacesPage } from "@/features/workspaces/pages/WorkspacesPage";
import { ErrorState } from "@/features/devices/components/ErrorState";
import { AuthProvider, useAuth } from "@/features/auth/AuthContext";
import { LoginPage } from "@/features/auth/pages/LoginPage";
import { ConfiguracoesPage } from "@/features/settings/pages/ConfiguracoesPage";
import { AvisoPainelRecebido } from "@/features/users/AvisoPainelRecebido";
import { useModalA11y } from "@/lib/useModalA11y";

import { Sidebar } from "./Sidebar";
import { PerformanceDiagnostics } from "./PerformanceDiagnostics";
import { NavigationErrorState } from "./NavigationErrorState";

// Editor e conversao de fuso so entram no navegador ao abrir configuracoes.
const WorkflowSettingsPage = lazy(() => import("@/features/shifts/WorkflowSettingsPage"));

/**
 * Casca principal. A `Sidebar` é fixa à esquerda; o conteúdo da rota
 * roda dentro do `.main` via `<Outlet />`. Cada página é responsável
 * pelo seu próprio `<PageHeader />` para poder customizar título,
 * breadcrumb e ações.
 *
 * Tier MOBILE (≤640px): a sidebar vira uma GAVETA sobreposta, aberta pelo
 * hambúrguer da `.m-topbar`. No desktop a topbar/backdrop têm `display:none`
 * e o shell permanece exatamente o grid `sidebar | main` de sempre — as
 * regras mobile vivem todas dentro de um `@media (max-width: 640px)`.
 */
export function AppShell() {
  const location = useLocation();
  const { usuario } = useAuth();
  const activeWorkflowId = getWorkflowIdFromLocation(location.pathname, location.search);
  const [navOpen, setNavOpen] = useState(false);
  const [mobileMode, setMobileMode] = useState(() => window.matchMedia?.("(max-width: 640px)").matches ?? false);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const drawerOpen = mobileMode && navOpen;
  const sidebarRef = useModalA11y<HTMLElement>({
    open: drawerOpen,
    onClose: () => setNavOpen(false),
    restoreFocus: mobileMode,
  });

  useEffect(() => {
    const media = window.matchMedia?.("(max-width: 640px)");
    if (!media) return;
    const onChange = () => {
      const sidebar = sidebarRef.current;
      const active = document.activeElement;
      if (media.matches && sidebar?.contains(active)) {
        // Antes de tornar a gaveta inerte, tira o foco do conteúdo escondido.
        menuButtonRef.current?.focus({ preventScroll: true });
      } else if (!media.matches && (active === menuButtonRef.current || active?.matches(".m-drawer-close"))) {
        // O acionador/fechar não existe visualmente no desktop.
        const target = sidebar?.querySelector<HTMLElement>('a[aria-current="page"]') ?? sidebar?.querySelector<HTMLElement>("a[href]");
        target?.focus({ preventScroll: true });
      }
      setMobileMode(media.matches);
      setNavOpen(false);
    };
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [sidebarRef]);

  // Navegar fecha a gaveta (cobre cliques em qualquer link da sidebar,
  // inclusive "Remover workflow", que termina em navigate()).
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname, location.search]);

  // O hook modal cuida de foco/Escape; o shell cuida apenas do scroll do fundo.
  useEffect(() => {
    if (!drawerOpen || typeof document === "undefined") return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [drawerOpen]);

  return (
    <div className="app">
      <header className="m-topbar">
        <button
          type="button"
          className="m-menu-btn"
          ref={menuButtonRef}
          aria-label="Abrir menu"
          aria-controls="ghm-primary-nav"
          aria-expanded={drawerOpen}
          onClick={() => {
            menuButtonRef.current?.focus({ preventScroll: true });
            setNavOpen(true);
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden
          >
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <span className="m-topbar-brand">
          <b>GHM</b>
          <small>Industrial Intel</small>
        </span>
      </header>

      {drawerOpen ? (
        <div
          className="m-backdrop"
          aria-hidden
          onClick={() => setNavOpen(false)}
        />
      ) : null}

      <Sidebar
        activeWorkflowId={activeWorkflowId}
        mobileOpen={drawerOpen}
        mobileMode={mobileMode}
        containerRef={sidebarRef}
        onMobileClose={() => setNavOpen(false)}
      />
      <main className="main" {...{ inert: drawerOpen ? "" : undefined }}>
        <Outlet />
        <AvisoPainelRecebido userId={usuario?.id ?? null} />
      </main>
      <PerformanceDiagnostics />
    </div>
  );
}

function getWorkflowIdFromLocation(pathname: string, search: string): string | null {
  const match = /^\/(?:workflows|workspaces)\/([^/]+)/.exec(pathname);
  if (match) return decodeURIComponent(match[1]);

  const params = new URLSearchParams(search);
  // Sidebar e DeviceDashboardPage usam `workspaceId` como contexto;
  // aceitar `workflowId` como sinonimo defensivo.
  return params.get("workspaceId") ?? params.get("workflowId");
}

/**
 * Decide entre o painel e a tela de login — e nao faz nem um nem outro
 * enquanto nao sabe.
 *
 * O terceiro estado importa. Sem ele, o primeiro quadro depois do refresh
 * mostraria a tela de login para quem JA esta logado, ate o `/api/auth/me`
 * responder. Piscar a tela de login em cada F5 ensina a pessoa a desconfiar do
 * sistema — e num painel de chao de fabrica, aberto o dia inteiro, isso
 * aconteceria muitas vezes por turno.
 */
function Porta() {
  const { estado } = useAuth();
  if (estado === null) {
    return (
      <div className="login-page">
        <div className="muted">Carregando...</div>
      </div>
    );
  }
  if (estado.modo === "anonimo") return <LoginPage />;
  return <AppShell />;
}

/**
 * `AuthProvider` fica AQUI, e nao em volta do `<RouterProvider>`, porque
 * `useAuth` precisa estar disponivel tambem para o `errorElement` — e o
 * `errorElement` so existe dentro da arvore da rota.
 */
function Raiz() {
  return (
    <AuthProvider>
      <Porta />
    </AuthProvider>
  );
}

function RootErrorBoundary() {
  const error = useRouteError() as unknown;
  // Não recarregar automaticamente: uma troca de release não autoriza perder
  // rascunhos. O operador decide quando buscar o documento e os assets atuais.
  return <NavigationErrorState error={error} onReload={() => window.location.reload()} />;
}

export const router = createBrowserRouter([
  {
    element: <Raiz />,
    errorElement: <RootErrorBoundary />,
    children: [
      { path: "/", element: <Navigate to="/workflows" replace /> },
      // Rotas publicas (Workflow = unidade operacional do cliente).
      { path: "/workflows", element: <WorkspacesPage /> },
      { path: "/workflows/:workflowId", element: <MachineDashboardPage /> },
      { path: "/workflows/:workflowId/configuracoes", element: <Suspense fallback={<div className="content" role="status">Carregando configurações…</div>}><WorkflowSettingsPage /></Suspense> },
      // Aliases legados (mantem URLs antigas funcionando enquanto o produto se renomeia).
      { path: "/workspaces", element: <Navigate to="/workflows" replace /> },
      { path: "/workspaces/:workspaceId", element: <WorkspacePage /> },
      { path: "/workspaces/:workspaceId/devices", element: <WorkspaceDevicesPage /> },
      {
        path: "/workspaces/:workspaceId/machines/:machineId",
        element: <MachineDashboardPage />,
      },
      // Configuracoes da instalacao. `/pessoas` existiu por algumas horas como
      // pagina propria; o redirect evita que um link ja compartilhado morra.
      { path: "/configuracoes", element: <ConfiguracoesPage /> },
      { path: "/pessoas", element: <Navigate to="/configuracoes" replace /> },
      // Rotas tecnicas.
      { path: "/devices", element: <DevicesPage /> },
      { path: "/devices/:deviceId", element: <DeviceDashboardPage /> },
      {
        path: "*",
        element: (
          <div className="content">
            <ErrorState
              title="Rota nao encontrada"
              message="A URL acessada nao existe nesta aplicacao."
            />
          </div>
        ),
      },
    ],
  },
]);
